// Biometrics notice screen (the `biometrics_notice` step). Twin of the desktop
// BiometricsNoticePage. Shown right after the begin screen whenever the researcher
// enabled provenance capture. It is a NOTICE, not a consent gate: the participant
// reads it and clicks Next to continue. There is no decline path.
//
// Submit payload: { acknowledged: true }.

import { useEffect, useRef } from "react";
import type { WebRendererProps } from "./registry";
import { PROVENANCE_NOTICE_FLAG } from "./survey/provenance";

export default function BiometricsNoticeRenderer({ onDone }: WebRendererProps) {
  const doneRef = useRef(false);

  // Mark provenance as active for this session (this screen only ever shows when
  // the researcher enabled it). SurveyForm reads this to decide whether to
  // capture + send content-blind typing provenance.
  useEffect(() => {
    try {
      sessionStorage.setItem(PROVENANCE_NOTICE_FLAG, "1");
    } catch {
      /* private mode / storage blocked — capture simply stays off */
    }
  }, []);

  function next() {
    if (doneRef.current) return; // re-entrance guard
    doneRef.current = true;
    onDone({ kind: "submit", result: { acknowledged: true } });
  }

  return (
    <div className="jol-biometrics">
      <div className="jol-biometrics-card">
        <h1 className="jol-biometrics-title">Before you begin</h1>
        <p>
          During this study, some information about <b>how</b> you respond may be
          collected. This includes the timing and rhythm of your typing and edits
          as you write open-text answers, and whether text was pasted.
        </p>
        <p>
          This is about <b>how</b> you respond, not <b>what</b> you say. The
          patterns of your typing may be recorded, and this can include
          biometric-style signals such as keystroke timing. Your written answers
          are saved as part of the study, as usual.
        </p>
        <p>
          You don't need to do anything differently. When you're ready, select{" "}
          <b>Next</b> to continue.
        </p>
        <div className="jol-biometrics-actions">
          <button type="button" className="jol-gate-submit" onClick={next}>
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
